import React from "react";
import classNames from "classnames/bind";
import { useTranslation } from "react-i18next";
import styles from "../authorization.module.scss";
import { EmailIcon, LockIcon, EyeIcon, UserIcon } from "../AuthIcons";
import { PasswordStrengthBar } from "../PasswordStrengthBar";

const cx = classNames.bind(styles);

interface InviteViewProps {
  email: string;
  displayName: string;
  password: string;
  confirmPassword: string;
  showPassword: boolean;
  showConfirmPassword: boolean;
  passwordError: boolean;
  confirmPasswordError: boolean;
  authError: string | null;
  isLoading: boolean;
  inviteValidating: boolean;
  inviteTokenValid: boolean | null;
  passwordStrength: number;
  passwordStrengthColor: string;
  passwordStrengthText: string;
  onDisplayNameChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onConfirmPasswordChange: (value: string) => void;
  onTogglePassword: () => void;
  onToggleConfirmPassword: () => void;
  onBackToSignIn: () => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
}

export const InviteView: React.FC<InviteViewProps> = ({
  email,
  displayName,
  password,
  confirmPassword,
  showPassword,
  showConfirmPassword,
  passwordError,
  confirmPasswordError,
  authError,
  isLoading,
  inviteValidating,
  inviteTokenValid,
  passwordStrength,
  passwordStrengthColor,
  passwordStrengthText,
  onDisplayNameChange,
  onPasswordChange,
  onConfirmPasswordChange,
  onTogglePassword,
  onToggleConfirmPassword,
  onBackToSignIn,
  onSubmit,
}) => {
  const { t } = useTranslation();

  // Loading while validating token
  if (inviteValidating || inviteTokenValid === null) {
    return (
      <div className={cx('form')}>
        <div className={cx('formHeader')}>
          <h2 className={cx('formTitle')}>{t("inviteValidating", "Validating invitation...")}</h2>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
          <span className={cx('spinner')}></span>
        </div>
      </div>
    );
  }

  // Invalid/expired token
  if (inviteTokenValid === false) {
    return (
      <div className={cx('form')}>
        <div className={cx('formHeader')}>
          <h2 className={cx('formTitle')}>{t("inviteInvalidTitle", "Invalid Invitation")}</h2>
          <p className={cx('formSubtitle')}>{t("inviteTokenInvalid", "This invitation link is invalid or has expired.")}</p>
        </div>

        <div className={cx('formFooter')}>
          <button
            type="button"
            onClick={onBackToSignIn}
            className={cx('backButton')}
          >
            &larr; {t("backToSignIn")}
          </button>
        </div>
      </div>
    );
  }

  // Valid token — show signup-like form
  return (
    <form className={cx('form')} onSubmit={onSubmit}>
      <div className={cx('formHeader')}>
        <h2 className={cx('formTitle')}>{t("createAccount")}</h2>
        <p className={cx('formSubtitle')}>{t("inviteSetPassword", "Set a password for your account")}</p>
      </div>

      {authError && <div className={cx('errorMessage')}>{t(authError, authError)}</div>}

      <div className={cx('inputGroup')}>
        <label className={cx('inputLabel')}>{t("email")}</label>
        <div className={cx('inputWrapper')}>
          <div className={cx('inputIcon')}>
            <EmailIcon />
          </div>
          <input
            name="email"
            type="email"
            className={cx('input')}
            value={email}
            readOnly
            style={{ opacity: 0.7, cursor: 'default' }}
          />
        </div>
      </div>

      <div className={cx('inputGroup')}>
        <label className={cx('inputLabel')}>{t("acceptInvite.name", "Your Name")}</label>
        <div className={cx('inputWrapper')}>
          <div className={cx('inputIcon')}>
            <UserIcon />
          </div>
          <input
            name="displayName"
            type="text"
            className={cx('input')}
            placeholder={t("acceptInvite.namePlaceholder", "Enter your name")}
            value={displayName}
            onChange={(e) => onDisplayNameChange(e.target.value)}
          />
        </div>
      </div>

      <div className={cx('inputGroup')}>
        <label className={cx('inputLabel')}>{t("password")}</label>
        <div className={cx('inputWrapper', { inputError: passwordError })}>
          <div className={cx('inputIcon')}>
            <LockIcon />
          </div>
          <input
            name="password"
            type={showPassword ? "text" : "password"}
            className={cx('input')}
            placeholder={t("enterPassword")}
            value={password}
            autoComplete="new-password"
            onChange={(e) => onPasswordChange(e.target.value)}
          />
          <button
            type="button"
            className={cx('passwordToggle')}
            onClick={onTogglePassword}
            tabIndex={-1}
          >
            <EyeIcon visible={showPassword} />
          </button>
        </div>
        {passwordError && <span className={cx('inputErrorText')}>{t("enterPassword")}</span>}
        {password && (
          <PasswordStrengthBar
            strength={passwordStrength}
            strengthColor={passwordStrengthColor}
            strengthText={passwordStrengthText}
          />
        )}
      </div>

      <div className={cx('inputGroup')}>
        <label className={cx('inputLabel')}>{t("confirmPassword")}</label>
        <div className={cx('inputWrapper', { inputError: confirmPasswordError })}>
          <div className={cx('inputIcon')}>
            <LockIcon />
          </div>
          <input
            name="confirmPassword"
            type={showConfirmPassword ? "text" : "password"}
            className={cx('input')}
            placeholder={t("confirmYourPassword")}
            value={confirmPassword}
            autoComplete="new-password"
            onChange={(e) => onConfirmPasswordChange(e.target.value)}
          />
          <button
            type="button"
            className={cx('passwordToggle')}
            onClick={onToggleConfirmPassword}
            tabIndex={-1}
          >
            <EyeIcon visible={showConfirmPassword} />
          </button>
        </div>
        {confirmPasswordError && <span className={cx('inputErrorText')}>{t("passwordsDoNotMatch")}</span>}
      </div>

      <button type="submit" className={cx('submitButton')} disabled={isLoading}>
        {isLoading ? <span className={cx('spinner')}></span> : t("signUp")}
      </button>

      <div className={cx('formFooter')}>
        <span className={cx('footerText')}>{t("alreadyHaveAccount")}</span>
        <button
          type="button"
          onClick={onBackToSignIn}
          className={cx('switchButton')}
        >
          {t("signIn")}
        </button>
      </div>
    </form>
  );
};
